import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Check, X, Clock, CheckCircle2, Calendar, Search, List, CalendarDays, Pencil, UserX } from "lucide-react";
import BookingsCalendar from "@/components/BookingsCalendar";

const STATUS_BADGE = {
  in_progress: { label: "In Bearbeitung", color: "var(--mv-accent)" },
  pending: { label: "Anfrage", color: "var(--mv-accent)" },
  confirmed: { label: "Bestätigt", color: "var(--mv-secondary)" },
  alternative_proposed: { label: "Alternativ vorgeschlagen", color: "#7c3aed" },
  cancelled: { label: "Storniert", color: "#dc2626" },
  completed: { label: "Erledigt", color: "var(--mv-text-muted)" },
};

const SORT_OPTIONS = [
  { value: "datetime_asc", label: "Datum & Zeit (aufsteigend)" },
  { value: "datetime_desc", label: "Datum & Zeit (absteigend)" },
  { value: "created_desc", label: "Neueste zuerst" },
  { value: "created_asc", label: "Älteste zuerst" },
  { value: "customer_asc", label: "Kunde (A–Z)" },
  { value: "status_asc", label: "Status" },
];

const STATUS_FILTERS = [
  { value: "all", label: "Alle Status" },
  { value: "open", label: "Offen (In Bearbeitung + Anfrage + Alternativ)" },
  { value: "confirmed", label: "Bestätigt" },
  { value: "completed", label: "Erledigt" },
  { value: "cancelled", label: "Storniert" },
];

// Format YYYY-MM-DD → DD.MM.YYYY (German locale)
function fmtDate(iso) {
  if (!iso) return "";
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) {
    try { return new Date(iso).toLocaleDateString("de-DE"); } catch { return iso; }
  }
  return `${m[3]}.${m[2]}.${m[1]}`;
}

export default function BookingsManager() {
  const { t } = useAuth();
  const [items, setItems] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [proposing, setProposing] = useState(null);
  const [proposal, setProposal] = useState({ date: "", time: "", message: "" });
  const [proposeAvail, setProposeAvail] = useState({}); // {staff_id: {status, reason, conflicting_time?}}
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({ staff_id: "", date: "", time: "", notes: "" });
  const [editError, setEditError] = useState("");
  const [editSaving, setEditSaving] = useState(false);
  const [editAvail, setEditAvail] = useState({}); // {staff_id: {status, reason, conflicting_time?}}
  const [searchParams] = useSearchParams();
  const initialStatus = searchParams.get("status") || "all";
  const [search, setSearch] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [sort, setSort] = useState("datetime_asc");
  const [statusFilter, setStatusFilter] = useState(initialStatus);
  const [view, setView] = useState("list"); // list | calendar

  const load = () => api.get("/cms/bookings").then(({ data }) => setItems(data));
  useEffect(() => {
    load();
    api.get("/cms/staff").then(({ data }) => setStaffList(data || []));
  }, []);

  // Live-Verfügbarkeit für Propose-Modal abrufen bei Änderung von Datum/Uhrzeit
  useEffect(() => {
    if (!proposing || !proposal.date || !proposal.time) { setProposeAvail({}); return; }
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/cms/bookings/staff-availability", {
          params: {
            date: proposal.date,
            time: proposal.time,
            service_id: proposing.service_id || "",
            exclude_id: proposing.id,
          },
        });
        if (!cancelled) setProposeAvail(data || {});
      } catch {
        if (!cancelled) setProposeAvail({});
      }
    }, 300);
    return () => { cancelled = true; clearTimeout(t); };
  }, [proposing, proposal.date, proposal.time]);

  // Live-Verfügbarkeit auch für Edit-Modal
  useEffect(() => {
    if (!editing || !editForm.date || !editForm.time) { setEditAvail({}); return; }
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/cms/bookings/staff-availability", {
          params: {
            date: editForm.date,
            time: editForm.time,
            service_id: editing.service_id || "",
            exclude_id: editing.id,
          },
        });
        if (!cancelled) setEditAvail(data || {});
      } catch {
        if (!cancelled) setEditAvail({});
      }
    }, 300);
    return () => { cancelled = true; clearTimeout(t); };
  }, [editing, editForm.date, editForm.time]);

  const action = async (id, act) => {
    await api.post(`/cms/bookings/${id}/${act}`);
    load();
  };
  const propose = async () => {
    try {
      await api.post(`/cms/bookings/${proposing.id}/propose`, proposal);
      setProposing(null);
      setProposal({ date: "", time: "", message: "", staff_id: "" });
      load();
    } catch (e) {
      alert(e?.response?.data?.detail || "Vorschlag konnte nicht gesendet werden");
    }
  };
  const openEdit = (b) => {
    setEditing(b);
    setEditForm({
      staff_id: b.staff_id || "",
      date: b.date || "",
      time: b.time || "",
      notes: b.notes || "",
    });
    setEditError("");
  };
  const saveEdit = async () => {
    if (!editing) return;
    setEditSaving(true);
    setEditError("");
    try {
      await api.put(`/cms/bookings/${editing.id}`, {
        staff_id: editForm.staff_id || null,
        date: editForm.date,
        time: editForm.time,
        notes: editForm.notes,
      });
      setEditing(null);
      load();
    } catch (e) {
      setEditError(e?.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally {
      setEditSaving(false);
    }
  };

  const visible = useMemo(() => {
    let xs = [...items];
    const q = search.trim().toLowerCase();
    if (q) {
      xs = xs.filter((b) =>
        (b.customer_name || "").toLowerCase().includes(q) ||
        (b.customer_email || "").toLowerCase().includes(q) ||
        (b.customer_phone || "").toLowerCase().includes(q) ||
        (b.service?.name || b.service_name || "").toLowerCase().includes(q) ||
        (b.staff?.name || "").toLowerCase().includes(q)
      );
    }
    if (dateFrom) xs = xs.filter((b) => (b.date || "") >= dateFrom);
    if (dateTo) xs = xs.filter((b) => (b.date || "") <= dateTo);
    if (statusFilter === "open") {
      xs = xs.filter((b) => ["in_progress", "pending", "alternative_proposed"].includes(b.status));
    } else if (statusFilter !== "all") {
      xs = xs.filter((b) => b.status === statusFilter);
    }
    const dtKey = (b) => `${b.date || "0000-00-00"} ${b.time || "00:00"}`;
    xs.sort((a, b) => {
      switch (sort) {
        case "datetime_asc": return dtKey(a).localeCompare(dtKey(b));
        case "datetime_desc": return dtKey(b).localeCompare(dtKey(a));
        case "created_desc": return (b.created_at || "").localeCompare(a.created_at || "");
        case "created_asc": return (a.created_at || "").localeCompare(b.created_at || "");
        case "customer_asc": return (a.customer_name || "").localeCompare(b.customer_name || "");
        case "status_asc": return (a.status || "").localeCompare(b.status || "");
        default: return 0;
      }
    });
    return xs;
  }, [items, search, dateFrom, dateTo, sort, statusFilter]);

  return (
    <div className="p-8 lg:p-12 mv-fade-in">
      <div className="mb-8 flex items-start justify-between flex-wrap gap-4">
        <div>
          <p className="mv-label mb-2">Terminanfragen</p>
          <h1 className="font-display text-4xl">{t("booking_plural")}</h1>
          <p className="text-sm mt-2" style={{ color: "var(--mv-text-muted)" }}>
            {visible.length === items.length ? `${items.length} Anfragen insgesamt` : `${visible.length} von ${items.length} Anfragen`}
          </p>
        </div>
        <div className="inline-flex rounded-full border p-1" style={{ borderColor: "var(--mv-border)" }} data-testid="view-toggle">
          <button
            data-testid="view-list"
            onClick={() => setView("list")}
            className="px-3 py-1.5 rounded-full text-xs flex items-center gap-1.5 transition-colors"
            style={{ background: view === "list" ? "var(--mv-primary)" : "transparent", color: view === "list" ? "#fff" : "var(--mv-text-muted)" }}
          >
            <List className="w-3.5 h-3.5" strokeWidth={1.8} /> Liste
          </button>
          <button
            data-testid="view-calendar"
            onClick={() => setView("calendar")}
            className="px-3 py-1.5 rounded-full text-xs flex items-center gap-1.5 transition-colors"
            style={{ background: view === "calendar" ? "var(--mv-primary)" : "transparent", color: view === "calendar" ? "#fff" : "var(--mv-text-muted)" }}
          >
            <CalendarDays className="w-3.5 h-3.5" strokeWidth={1.8} /> Kalender
          </button>
        </div>
      </div>

      <div className="mv-card p-3 md:p-4 mb-6 grid gap-3 md:grid-cols-[1fr_auto_auto_auto_auto] items-end" data-testid="bookings-filter-bar">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2" strokeWidth={1.5} style={{ color: "var(--mv-text-muted)" }} />
          <input
            data-testid="bookings-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Suche nach Name, E-Mail, Telefon, Leistung, Stylist"
            className="mv-input pl-9 text-sm"
          />
        </div>
        <div>
          <label className="block text-[10px] mb-1" style={{ color: "var(--mv-text-muted)" }}>Datum von</label>
          <input data-testid="bookings-date-from" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="mv-input text-sm" />
        </div>
        <div>
          <label className="block text-[10px] mb-1" style={{ color: "var(--mv-text-muted)" }}>Datum bis</label>
          <input data-testid="bookings-date-to" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="mv-input text-sm" />
        </div>
        <div>
          <label className="block text-[10px] mb-1" style={{ color: "var(--mv-text-muted)" }}>Status</label>
          <select data-testid="bookings-status-filter" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="mv-input text-sm">
            {STATUS_FILTERS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-[10px] mb-1" style={{ color: "var(--mv-text-muted)" }}>Sortierung</label>
          <select data-testid="bookings-sort" value={sort} onChange={(e) => setSort(e.target.value)} className="mv-input text-sm">
            {SORT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
        {(search || dateFrom || dateTo || statusFilter !== "all" || sort !== "datetime_asc") && (
          <button
            data-testid="bookings-clear-filter"
            onClick={() => { setSearch(""); setDateFrom(""); setDateTo(""); setStatusFilter("all"); setSort("datetime_asc"); }}
            className="md:col-span-full text-xs underline self-start"
            style={{ color: "var(--mv-text-muted)" }}
          >
            Filter zurücksetzen
          </button>
        )}
      </div>

      {proposing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(44,38,33,0.5)" }}>
          <div className="bg-white rounded-2xl p-6 w-full max-w-md" data-testid="propose-modal">
            <p className="mv-label mb-2">Alternativtermin vorschlagen</p>
            <h3 className="font-display text-xl mb-4">{proposing.customer_name}</h3>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><label className="block text-xs mb-1">Neues Datum</label><input type="date" required className="mv-input" value={proposal.date} onChange={(e) => setProposal({ ...proposal, date: e.target.value })} /></div>
                <div><label className="block text-xs mb-1">Neue Uhrzeit</label><input type="time" required className="mv-input" value={proposal.time} onChange={(e) => setProposal({ ...proposal, time: e.target.value })} /></div>
              </div>
              <div>
                <label className="block text-xs mb-1">Mitarbeiter</label>
                <select
                  data-testid="propose-staff"
                  className="mv-input text-sm"
                  value={proposal.staff_id ?? ""}
                  onChange={(e) => setProposal({ ...proposal, staff_id: e.target.value })}
                >
                  <option value="">— Nicht ändern / unverändert —</option>
                  <option value="unassigned">— Keinem Mitarbeiter zuordnen —</option>
                  {staffList.map((s) => {
                    const av = proposeAvail[s.id];
                    let prefix = "";
                    let suffix = "";
                    if (av?.status === "blocked") { prefix = "🔴 "; suffix = ` (${av.reason})`; }
                    else if (av?.status === "conflict") { prefix = "🟡 "; suffix = ` (Termin ${av.conflicting_time})`; }
                    else if (av?.status === "free") { prefix = "🟢 "; }
                    return (
                      <option key={s.id} value={s.id}>{prefix}{s.name}{suffix}</option>
                    );
                  })}
                </select>
                <p className="text-[11px] mt-1" style={{ color: "var(--mv-text-muted)" }}>
                  🟢 frei · 🟡 anderer Termin · 🔴 Urlaub/Krankheit · Wenn du einen Mitarbeiter wählst, wird der Termin automatisch ihm zugewiesen.
                </p>
              </div>
              <div><label className="block text-xs mb-1">Nachricht an Kunden (optional)</label><textarea className="mv-input min-h-[80px]" value={proposal.message} onChange={(e) => setProposal({ ...proposal, message: e.target.value })} /></div>
            </div>
            <div className="flex gap-2 mt-5 justify-end">
              <button onClick={() => setProposing(null)} className="mv-btn-ghost">Abbrechen</button>
              <button data-testid="propose-submit" onClick={propose} className="mv-btn">Vorschlag senden</button>
            </div>
          </div>
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(44,38,33,0.5)" }}>
          <div className="bg-white rounded-2xl p-6 w-full max-w-md" data-testid="edit-modal">
            <p className="mv-label mb-2">Buchung bearbeiten</p>
            <h3 className="font-display text-xl mb-1">{editing.customer_name}</h3>
            <p className="text-xs mb-4" style={{ color: "var(--mv-text-muted)" }}>
              {editing.service?.name || editing.service_name}
            </p>
            <div className="space-y-3">
              <div>
                <label className="block text-xs mb-1">Mitarbeiter</label>
                <select
                  data-testid="edit-staff"
                  className="mv-input text-sm"
                  value={editForm.staff_id}
                  onChange={(e) => setEditForm({ ...editForm, staff_id: e.target.value })}
                >
                  <option value="">— Nicht zugewiesen —</option>
                  {staffList.map((s) => {
                    const av = editAvail[s.id];
                    let prefix = "";
                    let suffix = "";
                    if (av?.status === "blocked") { prefix = "🔴 "; suffix = ` (${av.reason})`; }
                    else if (av?.status === "conflict") { prefix = "🟡 "; suffix = ` (Termin ${av.conflicting_time})`; }
                    else if (av?.status === "free") { prefix = "🟢 "; }
                    return (
                      <option key={s.id} value={s.id}>{prefix}{s.name}{suffix}</option>
                    );
                  })}
                </select>
                <p className="text-[11px] mt-1" style={{ color: "var(--mv-text-muted)" }}>
                  🟢 frei · 🟡 anderer Termin · 🔴 Urlaub/Krankheit
                </p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs mb-1">Datum</label>
                  <input data-testid="edit-date" type="date" className="mv-input text-sm" value={editForm.date} onChange={(e) => setEditForm({ ...editForm, date: e.target.value })} />
                </div>
                <div>
                  <label className="block text-xs mb-1">Uhrzeit</label>
                  <input data-testid="edit-time" type="time" className="mv-input text-sm" value={editForm.time} onChange={(e) => setEditForm({ ...editForm, time: e.target.value })} />
                </div>
              </div>
              <div>
                <label className="block text-xs mb-1">Notizen</label>
                <textarea data-testid="edit-notes" className="mv-input min-h-[80px] text-sm" value={editForm.notes} onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })} />
              </div>
              {editError && (
                <p className="text-xs px-3 py-2 rounded bg-red-50 text-red-700" data-testid="edit-error">{editError}</p>
              )}
            </div>
            <div className="flex gap-2 mt-5 justify-end">
              <button onClick={() => setEditing(null)} className="mv-btn-ghost" disabled={editSaving}>Abbrechen</button>
              <button data-testid="edit-save" onClick={saveEdit} className="mv-btn" disabled={editSaving}>
                {editSaving ? "Speichert…" : "Speichern"}
              </button>
            </div>
          </div>
        </div>
      )}

      {view === "calendar" ? (
        <BookingsCalendar
          bookings={visible}
          onPickDay={(iso) => { setDateFrom(iso); setDateTo(iso); setView("list"); }}
        />
      ) : visible.length === 0 ? (
        <div className="mv-card p-12 text-center">
          <Clock className="w-8 h-8 mx-auto mb-3 opacity-50" strokeWidth={1.5} />
          <p style={{ color: "var(--mv-text-muted)" }}>{items.length === 0 ? "Noch keine Buchungen." : "Keine Buchungen passen zum Filter."}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {visible.map((b) => {
            const badge = STATUS_BADGE[b.status] || STATUS_BADGE.in_progress;
            const isOpen = b.status === "in_progress" || b.status === "pending" || b.status === "alternative_proposed";
            const isConfirmed = b.status === "confirmed";
            return (
              <div key={b.id} className="mv-card p-5" data-testid={`booking-${b.id}`}>
                <div className="flex flex-wrap justify-between items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="font-display text-lg">{b.customer_name}</p>
                    <p className="text-xs mb-2" style={{ color: "var(--mv-text-muted)" }}>{b.customer_email} {b.customer_phone && `· ${b.customer_phone}`}</p>
                    <p className="text-sm">
                      <b>{b.service?.name || b.service_name || "Leistung"}</b>
                      {b.addons && b.addons.length > 0 && <span className="opacity-75"> + {b.addons.map((a) => a.name).join(", ")}</span>}
                      <span> · {fmtDate(b.date)} um {b.time}</span>
                      {b.staff?.name ? (
                        <span className="opacity-70"> · {b.staff.name}</span>
                      ) : (
                        <span
                          className="ml-2 inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full"
                          style={{ background: "#fef3c7", color: "#92400e" }}
                          data-testid={`unassigned-${b.id}`}
                        >
                          <UserX className="w-3 h-3" strokeWidth={1.8} /> Kein Mitarbeiter
                        </span>
                      )}
                    </p>
                    {b.status === "alternative_proposed" && b.proposed_date && (
                      <p className="text-xs mt-2 px-2 py-1 inline-block rounded" style={{ background: "rgba(124,58,237,0.1)", color: "#7c3aed" }}>
                        Vorschlag: {fmtDate(b.proposed_date)} um {b.proposed_time}
                      </p>
                    )}
                    {b.notes && <p className="text-xs mt-2 italic" style={{ color: "var(--mv-text-muted)" }}>&ldquo;{b.notes}&rdquo;</p>}
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <span className="text-xs px-2.5 py-1 rounded-full" style={{ background: `${badge.color}22`, color: badge.color }}>{badge.label}</span>
                    <div className="flex items-center gap-1">
                      <button
                        data-testid={`edit-${b.id}`}
                        onClick={() => openEdit(b)}
                        className="p-2 rounded-full hover:bg-[var(--mv-surface)]"
                        title="Bearbeiten"
                      >
                        <Pencil className="w-4 h-4" strokeWidth={1.5} style={{ color: "var(--mv-text-muted)" }} />
                      </button>
                      {isOpen && (
                        <>
                          <button data-testid={`confirm-${b.id}`} onClick={() => action(b.id, "confirm")} className="p-2 rounded-full hover:bg-[var(--mv-surface)]" title="Bestätigen">
                            <Check className="w-4 h-4" strokeWidth={1.5} style={{ color: "var(--mv-secondary)" }} />
                          </button>
                          <button data-testid={`propose-${b.id}`} onClick={() => { setProposing(b); setProposal({ date: b.date, time: b.time, message: "", staff_id: b.staff_id || "" }); }} className="p-2 rounded-full hover:bg-[var(--mv-surface)]" title="Alternativtermin">
                            <Calendar className="w-4 h-4" strokeWidth={1.5} style={{ color: "#7c3aed" }} />
                          </button>
                          <button data-testid={`cancel-${b.id}`} onClick={() => action(b.id, "cancel")} className="p-2 rounded-full hover:bg-red-50" title="Stornieren">
                            <X className="w-4 h-4 text-red-500" strokeWidth={1.5} />
                          </button>
                        </>
                      )}
                      {isConfirmed && (
                        <button data-testid={`complete-${b.id}`} onClick={() => action(b.id, "complete")} className="p-2 rounded-full hover:bg-[var(--mv-surface)]" title="Als erledigt markieren">
                          <CheckCircle2 className="w-4 h-4" strokeWidth={1.5} style={{ color: "var(--mv-text-muted)" }} />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
